import { useState, useEffect } from "react";
import { Category, Product } from "../../pages/WareHouseMgt/types";
import { BiX } from "react-icons/bi";
import axios from "axios";
import { useAppSelector } from "../../redux/hooks";
import toast from "react-hot-toast";

interface AddProductModalProps {
  storeId: string;
  product: Product | null;
  onClose: () => void;
  onProductAdded: (product: Product) => void;
  warehouseId?: string;
  storeName?: string;
  location?: string;
}

export default function AddProductModal({
  storeId,
  product,
  onClose,
  onProductAdded,
  warehouseId,
  storeName,
  location,
}: AddProductModalProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const { token } = useAppSelector((store) => store.auth);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    price: "",
    quantity: "",
    measurement: "",
    image: "",
    attribute: "Regular" as Product["attribtes"],
  });

  useEffect(() => {
    // Load categories (commented out for now since API is not set up)
    // const loadCategories = async () => {
    //   try {
    //     const data = await fetchCategories();
    //     setCategories(data);
    //   } catch (error) {
    //     console.error("Failed to fetch categories:", error);
    //   } finally {
    //     setLoading(false);
    //   }
    // };

    // loadCategories();

    // Pre-populate form data when editing a product
    if (product) {
      // Format dates properly for date inputs
      const formatDateForInput = (dateString?: string) => {
        if (!dateString) return "";
        try {
          const date = new Date(dateString);
          return date.toISOString().split("T")[0];
        } catch {
          return "";
        }
      };

      setFormData({
        name: product.name || "",
        category: product.type || "", // Note: using 'type' from Product interface
        price: product.price?.toString() || "",
        quantity: product.quantity?.toString() || "",
        measurement: "", // Measurement not available in edit mode
        image: product.image || "",
        attribute: product.attribtes || "Regular",
      });
    } else {
      // Reset form for new product
      setFormData({
        name: "",
        category: "",
        price: "",
        quantity: "",
        measurement: "",
        image: "",
        attribute: "Regular",
      });
    }
  }, [product]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a valid image file (JPEG, PNG, GIF, WEBP)");
      return;
    }

    setUploadingImage(true);
    setError(null);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append("file", file);
      uploadFormData.append("type", "image"); // Backend expects 'image', 'video', or 'pdf'

      const uploadUrl =
        process.env.REACT_APP_RESTAURANT_UPLOAD_URL ||
        (process.env.REACT_APP_RESTAURANT_URL
          ? `${process.env.REACT_APP_RESTAURANT_URL.replace(/\/v1\/api\/?$/, "").replace(/\/+$/, "")}/upload/file`
          : "https://api.dev.foodbank4u.com/restaurant/upload/file");

      const response = await axios.post(uploadUrl, uploadFormData, {
        headers: {
          Authorization: token,
          // Don't set Content-Type manually - axios will set it with boundary
        },
      });

      if (response.data && response.data.url) {
        setFormData((prev) => ({ ...prev, image: response.data.url }));
        toast.success("Image uploaded successfully!");
      } else {
        toast.error("Image uploaded but no URL received");
      }
    } catch (error: any) {
      console.error("Failed to upload image:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to upload image. Please try again.";
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitLoading(true);
    setError(null);

    try {
      let productData;
      let response;
      let updatedProduct;

      if (product) {
        // For editing: only send editable fields (price and quantity)
        productData = {
          price: parseFloat(formData.price),
          quantity: parseInt(formData.quantity),
        };

        // Update existing product
        response = await axios.put(
          `${process.env.REACT_APP_API_URL}/update-product/${product._id}`,
          productData,
          {
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
            },
          }
        );
        updatedProduct = response.data.product || {
          ...product,
          ...productData,
        };
      } else {
        // For adding new product: send required fields
        productData = {
          name: formData.name,
          type: formData.category, // Map form 'category' field to API 'type' field
          price: parseFloat(formData.price),
          location: location || "", // Add location from warehouse/store
          quantity: parseInt(formData.quantity),
          measurement: formData.measurement,
          image: formData.image || "/placeholder.svg?height=100&width=100",
        };

        // Add new product - use correct endpoint format
        response = await axios.post(
          `${process.env.REACT_APP_API_URL}/add-warehouse-product/${warehouseId}/${storeName}`,
          productData,
          {
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
            },
          }
        );
        updatedProduct = response.data.product || {
          ...productData,
          _id: response.data._id || Date.now().toString(),
        };
      }

      // Show success toast
      toast.success(
        product
          ? "Product updated successfully!"
          : "Product added successfully!"
      );

      onProductAdded(updatedProduct);
    } catch (error: any) {
      console.error("Failed to add/update product:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        `Failed to ${product ? "update" : "add"} product. Please try again.`;

      // Show error toast
      toast.error(errorMessage);

      setError(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">
            {product ? "Edit Product" : "New Product"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <BiX size={24} />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-600"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {product && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">Edit Mode:</span> You can only
                  edit Price and Quantity.
                </p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Product Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter product name"
                  className={`w-full px-3 py-2 border rounded-md ${
                    product ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                  disabled={!!product}
                  required
                />
                {product && (
                  <p className="text-xs text-gray-500 mt-1">
                    Product name cannot be changed when editing
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="category"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Category
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md ${
                    product ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                  disabled={!!product}
                  required
                >
                  <option value="">Select product category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                  <option value="Vegetable">Vegetable</option>
                  <option value="Fruits">Fruits</option>
                  <option value="Grain">Grain</option>
                  <option value="Drinks">Drinks</option>
                  <option value="Dietary">Dietary</option>
                  <option value="Meat">Meat</option>
                </select>
                {product && (
                  <p className="text-xs text-gray-500 mt-1">
                    Category cannot be changed when editing
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="price"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Price
                </label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="Enter price"
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
                {product && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ This field can be edited
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="quantity"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Quantity
                </label>
                <input
                  type="number"
                  id="quantity"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  placeholder="Enter product quantity"
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
                {product && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ This field can be edited
                  </p>
                )}
              </div>

              {!product && (
                <div>
                  <label
                    htmlFor="measurement"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Measurement/Unit
                  </label>
                  <input
                    type="text"
                    id="measurement"
                    name="measurement"
                    value={formData.measurement}
                    onChange={handleChange}
                    placeholder="e.g., kg, lbs, pieces, liters"
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter the unit of measurement (e.g., kg, lbs, pieces)
                  </p>
                </div>
              )}

              {!product && (
                <div>
                  <label
                    htmlFor="image"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Image
                  </label>
                  <div className="space-y-2">
                    <input
                      type="file"
                      id="imageFile"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                      className="w-full px-3 py-2 border rounded-md file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100 disabled:opacity-50"
                    />
                    {uploadingImage && (
                      <p className="text-sm text-blue-600 flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-600 mr-2"></div>
                        Uploading image...
                      </p>
                    )}
                    {formData.image && !uploadingImage && (
                      <div className="flex items-center space-x-2">
                        <p className="text-sm text-green-600">
                          ✓ Image uploaded successfully
                        </p>
                        <button
                          type="button"
                          onClick={() =>
                            setFormData((prev) => ({ ...prev, image: "" }))
                          }
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    <p className="text-xs text-gray-500">
                      Or enter image URL directly:
                    </p>
                    <input
                      type="text"
                      id="image"
                      name="image"
                      value={formData.image}
                      onChange={handleChange}
                      placeholder="https://example.com/image.jpg"
                      disabled={uploadingImage}
                      className="w-full px-3 py-2 border rounded-md disabled:opacity-50"
                    />
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="mt-4 text-center text-sm text-red-600 bg-red-50 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="flex justify-end space-x-4 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border rounded-md"
                disabled={submitLoading}
              >
                Discard
              </button>
              <button
                type="submit"
                disabled={submitLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {submitLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    {product ? "Updating..." : "Adding..."}
                  </>
                ) : (
                  <>{product ? "Update Product" : "Add Product"}</>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
